import React, { useState, useEffect, useCallback, useMemo } from "react"; // Added useCallback and useMemo
import { attendanceAPI } from "../../../../services/AdminPortal/staffworkAPI";
import { ChevronLeft, ChevronRight } from "lucide-react";

const StaffattendanceTable = ({
  setSelectedStaffMembers,
  selectedStaffMembers,
  handleViewStaffProfile,
  ITEMS_PER_PAGE,
  searchTerm,
  selectedRoleFilter,
  getDisplayProfilePicUrl,
}) => {
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedAll, setSelectedAll] = useState(false);

  // Memoize the data fetching function
  // console.log(summaryData,"jjjjjjj")
  const fetchSummaries = useCallback(
    async (pageToFetch = 1) => {
      setLoading(true);
      setError(null);
      try {
        const response = await attendanceAPI.getAllStaffWorkfolioSummary({
          page: pageToFetch,
          limit: ITEMS_PER_PAGE,
        });
        setSummaryData(response.data || []);
        if (response.pagination) {
          setCurrentPage(response.pagination.currentPage);
          setTotalPages(response.pagination.totalPages);
        } else {
          setTotalPages(1); // Default if no pagination info
        }
      } catch (err) {
        setError(err.message || "Failed to fetch staff attendance summaries");
        setSummaryData([]); // Clear data on error
      } finally {
        setLoading(false);
      }
    },
    [ITEMS_PER_PAGE]
  ); // Dependency: ITEMS_PER_PAGE affects the API call

  // Effect to fetch data when currentPage or ITEMS_PER_PAGE changes
  useEffect(() => {
    fetchSummaries(currentPage);
  }, [currentPage, fetchSummaries]); // Dependency: currentPage and the memoized fetchSummaries

  // Memoize pagination functions
  const goToPage = useCallback(
    (pageNumber) => {
      if (pageNumber >= 1 && pageNumber <= totalPages) {
        setCurrentPage(pageNumber);
      }
    },
    [totalPages]
  );

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((prevPage) => Math.max(1, prevPage - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((prevPage) => Math.min(totalPages, prevPage + 1));
  }, [totalPages]);

  // Filter and search data
  const filteredAndSearchedData = useMemo(() => {
    const lowerSearch = searchTerm?.toLowerCase() || "";

    return summaryData.filter(
      (staff) =>
        (staff.staffName?.toLowerCase().includes(lowerSearch) ||
          staff.staffId?.toLowerCase().includes(lowerSearch) ||
          staff.branchName?.toLowerCase().includes(lowerSearch)) &&
        (selectedRoleFilter === "All" || staff.role === selectedRoleFilter)
    );
  }, [summaryData, searchTerm, selectedRoleFilter]);

  // Derive the IDs of the current page for the "select all" checkbox
  const currentPageStaffIds = filteredAndSearchedData
    .map((staff) => staff.staffId)
    .filter(Boolean); // Ensure IDs are valid

  // Local selection handlers
  const handleLocalSelectAll = useCallback(() => {
    const newSelectedAll = !selectedAll;
    setSelectedAll(newSelectedAll);
    if (newSelectedAll) {
      setSelectedStaffMembers(filteredAndSearchedData.map((s) => s.staffId));
    } else {
      setSelectedStaffMembers([]);
    }
  }, [selectedAll, filteredAndSearchedData, setSelectedStaffMembers]);

  const handleLocalCheckboxChange = useCallback(
    (staffId) => {
      setSelectedStaffMembers((prev) => {
        const updatedSelection = prev.includes(staffId)
          ? prev.filter((id) => id !== staffId)
          : [...prev, staffId];

        setSelectedAll(
          filteredAndSearchedData.length > 0 &&
            updatedSelection.length === filteredAndSearchedData.length
        );
        return updatedSelection;
      });
    },
    [filteredAndSearchedData, setSelectedStaffMembers]
  );

  if (loading) {
    return (
      <div className="min-h-[300px] bg-[#131313] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error) {
    return <p className="text-center text-red-500 py-4">Error: {error}</p>;
  }

  return (
    <div className="p-4">
      <div className="bg-[#131313] rounded-lg border border-[#333333] w-full overflow-x-auto scrollbar-hide">
        <table className="min-w-full">
          <thead className="border-y border-[#333333]">
            <tr className="text-white">
              <th className="p-4 text-center border-2 border-[#333333]">
                <input
                  type="checkbox"
                  className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:bg-transparent checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                    before:content-['✓'] before:absolute before:text-[#ffca20] before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                  checked={selectedAll}
                  onChange={handleLocalSelectAll}
                  disabled={filteredAndSearchedData.length === 0} // Disable if no data
                />
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                STAFF PROFILE
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                ROLE
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                BRANCH NAME
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                WORKING DAYS
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                TASK PROGRESS
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                LEAVES TAKEN
              </th>
              <th className="text-[#F3F3F3] border-b border-r border-[#333333] pb-2 pr-4">
                STATUS
              </th>

              <th className="p-4 text-center border-2 border-[#333333]">
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {filteredAndSearchedData.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-400">
                  No staff data found matching your criteria.
                </td>
              </tr>
            ) : (
              filteredAndSearchedData.map((staffSummary, index) => (
                <tr key={staffSummary.staffId || index} className="text-white">
                  <td className="p-4 text-center border-2 border-[#333333]">
                    <input
                      type="checkbox"
                      className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                        before:content-['✓'] before:absolute before:text-[#ffca20] before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                      checked={selectedStaffMembers.includes(
                        staffSummary.staffId
                      )}
                      onChange={() =>
                        handleLocalCheckboxChange(staffSummary.staffId)
                      }
                    />
                  </td>
                  <td className="p-4 border-l border-[#333333] lg:min-w-[200px]">
                    <div className="flex items-center">
                      <img
                        src={
                          staffSummary.profilePicture ||
                          getDisplayProfilePicUrl(null)
                        }
                        alt={staffSummary.staffName}
                        className="h-10 w-10 rounded-full mr-3 object-cover flex-shrink-0"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = getDisplayProfilePicUrl(null);
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold lg:truncate">
                          {staffSummary.staffName
                            .split(" ")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() +
                                word.slice(1).toLowerCase()
                            )
                            .join(" ")}
                        </p>
                        <p className="text-gray-400 lg:truncate">
                          {staffSummary.staffId}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 border-l border-[#333333]">
                    {staffSummary.role?.toUpperCase()}
                  </td>
                  <td className="p-4 text-center border-2 border-[#333333]">
                    {staffSummary?.branchName || "N/A"}
                  </td>
                  <td className="p-4 border-l border-[#333333]">
                    {staffSummary.workingDays || 0} /{" "}
                    {staffSummary.totalExpectedDays || 0} days
                  </td>
                  <td className="p-4 border-l border-[#333333]">
                    {staffSummary.tasksCompleted || 0} /{" "}
                    {staffSummary.totalTasks || 0}
                  </td>
                  <td className="p-4 border-l border-[#333333]">
                    {staffSummary.leavesTaken || 0}
                  </td>
                  <td className="p-4 border-l border-[#333333]">
                    <p
                      className={`${
                        staffSummary?.status?.toLowerCase() === "active"
                          ? "bg-green-500 bg-opacity-20 text-green-500"
                          : staffSummary?.status?.toLowerCase() === "pending"
                          ? "bg-orange-500 bg-opacity-20 text-orange-500"
                          : "bg-[#FFCA20] bg-opacity-20 text-[#FFCA20]"
                      } px-3 py-1 rounded-full border-none focus:outline-none`}
                    >
                      {staffSummary?.status}
                    </p>
                  </td>

                  <td className="p-4 border-l border-[#333333]">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                      <button
                        className="text-[#FFCA20] underline text-sm sm:text-base"
                        onClick={() =>
                          handleViewStaffProfile(staffSummary.staffId)
                        }
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6 mb-4">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className="flex items-center text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} className="mr-1" />
            Previous
          </button>
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => goToPage(pageNumber)}
                  className={`w-8 h-8 flex items-center justify-center rounded ${
                    currentPage === pageNumber
                      ? "bg-[#FFCA20] text-black font-medium"
                      : "bg-gray-800 text-white hover:bg-gray-700"
                  }`}
                >
                  {pageNumber}
                </button>
              )
            )}
          </div>
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className="flex items-center text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight size={16} className="ml-1" />
          </button>
        </div>
      )}
    </div>
  );
};

export default StaffattendanceTable;
